// Environment type definition
type Environment = "dev" | "test" | "test2" | "prod";

// Base configuration interface
export interface IConfig {
  api: {
    src: string;
    jumbomailApiAuthCookie: string;
    jumbomailAuthCookie: string;
    downloader: string;
    resource: string;
    bucket: string;
  };
  env: {
    type: Environment;
    src: string;
  };
  baseurl: string;
  supportmail: string;
  billingurl: string;
}

export interface IConfigSet {
  [key: string]: IConfig;
}

// Helper function to get domain-based URLs
const getDomainBasedUrls = (hostname: string, isProd: boolean) => ({
  src: `https://api.${isProd ? hostname : "jumbomail.me"}`,
  resource: `https://resources.${isProd ? hostname : "jumbomail.me"}`,
  bucket: `files.${isProd ? hostname : "jumbomail.me"}`,
});

// Base configuration that's shared across environments
const baseConfig = {
  supportmail: "support@jumbomail.me",
  jumbomailApiAuthCookie: "JumboMail_API_Auth",
  jumbomailAuthCookie: "Jumbomail_Authentication",
};

// make sure that the hostname not include www. if it include www. remove it
const hostname = window.location.hostname.replace("www.", "");

const configSet: IConfigSet = {
  dev: {
    api: {
      ...getDomainBasedUrls(hostname, false), // change to false for defaule local env
      jumbomailApiAuthCookie: baseConfig.jumbomailApiAuthCookie,
      jumbomailAuthCookie: baseConfig.jumbomailAuthCookie,
      downloader: "https://www.jumbomail.me/en/",
    },
    env: {
      type: "dev",
      src: "https://www.jumbomail.me/en",
    },
    baseurl: `http://${hostname}`,
    supportmail: baseConfig.supportmail,
    billingurl: "http://localhost:5000",
  },
  prod: {
    api: {
      ...getDomainBasedUrls(hostname, true),
      jumbomailApiAuthCookie: baseConfig.jumbomailApiAuthCookie,
      jumbomailAuthCookie: baseConfig.jumbomailAuthCookie,
      downloader: "https://www.jumbomail.me/en/",
    },
    env: {
      type: "prod",
      src: "https://jumbomail.me/en",
    },
    baseurl: `https://${hostname}`,
    supportmail: baseConfig.supportmail,
    billingurl: "https://billing-prod.jumbomail.me/",
  },
  test: {
    api: {
      src: "https://api.test.jumbomail.me",
      jumbomailApiAuthCookie: `Test-${baseConfig.jumbomailApiAuthCookie}`,
      jumbomailAuthCookie: `Test-${baseConfig.jumbomailAuthCookie}`,
      downloader: "https://test.jumbomail.me/en/",
      resource: "https://resources.jumbomail.me",
      bucket: "files.jumbomail.me",

      //...getDomainBasedUrls(hostname, false),
    },
    env: {
      type: "test",
      src: "https://test.jumbomail.me/en",
    },
    baseurl: "https://test.jumbomail.me",
    supportmail: baseConfig.supportmail,
    billingurl: "https://billing-test.jumbomail.me/",
  },
  test2: {
    api: {
      src: "https://testapi2.jumbomail.me",
      jumbomailApiAuthCookie: `Test-${baseConfig.jumbomailApiAuthCookie}`,
      jumbomailAuthCookie: `Test-${baseConfig.jumbomailAuthCookie}`,
      downloader: "https://test.jumbomail.me/en/",
      ...getDomainBasedUrls(
        hostname.replace("test2.jumbomail.me", "test.jumbomail.me"),
        false
      ),
    },
    env: {
      type: "test",
      src: "https://test2.jumbomail.me/en",
    },
    baseurl: "https://test2.jumbomail.me",
    supportmail: baseConfig.supportmail,
    billingurl: "https://billing-test.jumbomail.me/",
  },
};

// Get environment from REACT_APP_STAGE or default to dev
const getEnvironment = (): Environment => {
  const stage = process.env.REACT_APP_STAGE?.trim() || "";
  return (
    Object.keys(configSet).includes(stage) ? stage : "prod"
  ) as Environment;
};

const config: IConfig = configSet[getEnvironment()];

export default config;
